/**
 * Browser Notification Service for Code Arena
 * Handles requesting permission and showing notifications when the tab is open
 */

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return "Notification" in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return "unsupported";
  return Notification.permission; // 'granted', 'denied', or 'default'
};

// Request notification permission from user
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn("Notifications are not supported in this browser");
    return "unsupported";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return "error";
  }
};

// Show a browser notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported()) return null;
  if (Notification.permission !== "granted") return null;

  const defaultOptions = {
    icon: "/logo192.png",
    badge: "/logo192.png",
    vibrate: [200, 100, 200],
    tag: "codearena-notification",
    renotify: true,
    requireInteraction: false,
    silent: false,
    ...options,
  };

  try {
    const notification = new Notification(title, defaultOptions);

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  } catch (error) {
    console.error("Error showing notification:", error);
    return null;
  }
};

// Show new job notification
export const showNewJobNotification = (job) => {
  if (!job) return null;

  const title = `🎉 New ${job.type || "Job"} Alert!`;
  const body = `${job.title} at ${job.companyName}${job.locations?.length ? ` - ${job.locations[0]}` : ""}`;

  const notification = showNotification(title, {
    body,
    icon: job.companyLogo || "/logo192.png",
    tag: `job-${job.id}`,
    data: { jobId: job.id, url: `/jobs/${job.id}` },
  });

  if (notification) {
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      window.location.href = `/jobs/${job.id}`;
      notification.close();
    };
  }

  return notification;
};

// Show multiple new jobs notification
export const showMultipleJobsNotification = (count, jobs = []) => {
  if (count <= 0) return null;

  const title = `🔔 ${count} New Job${count > 1 ? "s" : ""} Posted!`;
  const companies = [...new Set(jobs.slice(0, 3).map(j => j.companyName))].join(", ");
  const body = companies 
    ? `From ${companies}${count > 3 ? ` and ${count - 3} more` : ""}`
    : `Check out the latest opportunities`;

  const notification = showNotification(title, {
    body,
    tag: "new-jobs-batch",
    data: { url: "/jobs" },
  });

  if (notification) {
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      window.location.href = "/jobs";
      notification.close();
    };
  }

  return notification;
};

export default {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  showNewJobNotification,
  showMultipleJobsNotification,
};
